import { createRoute } from "@tanstack/react-router";
import { authenticatedRoute } from "../_authenticated";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
} from "@/components/ui";
import { useAuth } from "@/contexts/useAuth";
import { useCrmStatus } from "@/hooks/useCrmStatus";
import { apiDelete, apiPost, trackEvent } from "@/api";
import { getApiUrl } from "@/config";
import { cn, toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";

const PROVIDER_IDS = ["hubspot", "pipedrive", "salesforce", "weclapp"] as const;
const PROVIDER_COLORS: Record<(typeof PROVIDER_IDS)[number], string> = {
  hubspot: "bg-amber-500",
  pipedrive: "bg-green-500",
  salesforce: "bg-blue-600",
  weclapp: "bg-teal-500",
};
const PROVIDER_OAUTH: Record<(typeof PROVIDER_IDS)[number], boolean> = {
  hubspot: true,
  pipedrive: true,
  salesforce: true,
  weclapp: false,
};

function CrmLogo({ label, color }: { label: string; color: string }) {
  const initial = toDisplayString(label).slice(0, 1).toUpperCase();
  return (
    <div
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white",
        color
      )}
      aria-hidden
    >
      {initial}
    </div>
  );
}

function CrmPage() {
  const { t, i18n } = useTranslation("common");
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : "";
  const { data: connections, isLoading, isFetching, error, refetch } = useCrmStatus();
  const locale = i18n.language || "en";

  const [oauthError, setOauthError] = useState<string | null>(null);
  const toastedRef = useRef(false);
  useEffect(() => {
    if (toastedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const errorParam = params.get("error");
    if (connected) {
      toastedRef.current = true;
      trackEvent("crm_connected", token || null, { provider: connected }).catch(() => {});
      const label = toDisplayString(
        PROVIDER_IDS.includes(connected as (typeof PROVIDER_IDS)[number])
          ? t(`overview.provider_${connected}` as "overview.provider_hubspot")
          : connected
      );
      toast.success(t("crm.connectedSuccess", { provider: label }));
      window.history.replaceState({}, "", window.location.pathname);
    } else if (errorParam) {
      toastedRef.current = true;
      setOauthError(errorParam);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [t]);
  const base = getApiUrl().replace(/\/$/, "");
  const connectedSet = new Set((connections ?? []).map((c) => c.provider));
  const connectionByProvider = new Map((connections ?? []).map((c) => [c.provider, c]));
  const [connecting, setConnecting] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [weclappTenant, setWeclappTenant] = useState("");
  const [weclappToken, setWeclappToken] = useState("");

  const formatDate = (date: Date) => new Date(date).toLocaleDateString(toDisplayString(locale));
  const getLabel = (providerId: string): string =>
    toDisplayString(
      PROVIDER_IDS.includes(providerId as (typeof PROVIDER_IDS)[number])
        ? t(`overview.provider_${providerId}` as "overview.provider_hubspot")
        : providerId
    );

  const startConnect = async (provider: string) => {
    setConnecting(provider);
    try {
      const res = await fetch(`${base}/crm/connect/${provider}/start`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = (await res.json()) as {
        ok?: boolean;
        authUrl?: string;
        error?: { code?: string; message?: string };
      };
      if (data.error?.code === "integration_limit") {
        toast.error(t("crm.integrationLimit"));
        return;
      }
      if (data.ok && data.authUrl) {
        window.open(data.authUrl, "_blank", "noopener");
      } else {
        toast.error(toErrorDisplayMessage(data.error?.message) || t("crm.connectStartError"));
      }
    } catch {
      toast.error(t("crm.connectStartError"));
    } finally {
      setConnecting(null);
    }
  };

  const connectWeclapp = async () => {
    if (!weclappTenant.trim() || !weclappToken.trim()) {
      toast.error(t("crm.enterTenantAndToken"));
      return;
    }
    setConnecting("weclapp");
    try {
      const res = await apiPost<{ configured?: boolean }>(
        "/crm/connect/weclapp/configure",
        token,
        { apiToken: weclappToken.trim(), tenantDomain: weclappTenant.trim() }
      );
      if (res.ok && res.data?.configured) {
        toast.success(t("crm.weclappConnected"));
        setWeclappTenant("");
        setWeclappToken("");
        void refetch();
      } else {
        toast.error(t("crm.weclappError"));
      }
    } finally {
      setConnecting(null);
    }
  };

  const disconnect = async (provider: string) => {
    const label = getLabel(provider);
    if (!window.confirm(toDisplayString(t("crm.disconnectConfirm", { provider: label })))) return;
    setDisconnecting(provider);
    try {
      const res = await apiDelete(`/crm/connections/${provider}`, token);
      if (res.ok) {
        toast.success(t("crm.disconnected", { provider: label }));
        void refetch();
      } else {
        toast.error(t("crm.disconnectFailed"));
      }
    } finally {
      setDisconnecting(null);
    }
  };

  return (
    <div className="space-y-6">
      {oauthError && (
        <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
          <p className="font-medium">{toSafeReactNode(t("crm.connectFailed"))}</p>
          <p className="mt-1 text-danger/80">{toSafeReactNode(t("crm.connectFailedDetail"))}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button size="sm" variant="primary" onClick={() => { setOauthError(null); }}>
              {toSafeReactNode(t("crm.tryAgain"))}
            </Button>
            <a href="mailto:support@takelead.io" className="text-xs text-text-muted hover:underline">
              {toSafeReactNode(t("crm.contactSupport"))}
            </a>
          </div>
        </div>
      )}
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("crm.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("crm.subtitle"))}</p>
      </div>

      {isLoading && (
        <p className="text-sm text-text-muted">{toSafeReactNode(t("crm.loading"))}</p>
      )}
      {error && (
        <p className="mb-4 text-sm text-danger">{toErrorDisplayMessage(error)}</p>
      )}

      {connections && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {PROVIDER_IDS.map((id) => {
              const label = getLabel(id);
              const color = PROVIDER_COLORS[id];
              const oauth = PROVIDER_OAUTH[id];
              const connected = connectedSet.has(id);
              const conn = connectionByProvider.get(id);

              return (
                <Card key={toDisplayString(id)}>
                  <CardHeader className="flex flex-row items-center gap-3 pb-2">
                    <CrmLogo label={label} color={color} />
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base">{toSafeReactNode(label)}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "h-2 w-2 shrink-0 rounded-full",
                            connected ? "bg-success shadow-[0_0_8px_var(--color-success)]" : "bg-text-subtle"
                          )}
                          aria-hidden
                        />
                        <span className="text-xs text-text-muted">
                          {connected
                            ? conn?.connectedAt
                              ? toSafeReactNode(t("crm.connectedSince", { date: formatDate(new Date(conn.connectedAt)) }))
                              : toSafeReactNode(t("crm.connected"))
                            : toSafeReactNode(t("crm.notConnected"))}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    {connected ? (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={disconnecting === id}
                        onClick={() => disconnect(id)}
                      >
                        {disconnecting === id ? toSafeReactNode(t("crm.disconnecting")) : toSafeReactNode(t("crm.disconnect"))}
                      </Button>
                    ) : oauth ? (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={connecting === id}
                        onClick={() => startConnect(id)}
                      >
                        {connecting === id ? "…" : toSafeReactNode(t("crm.connect"))}
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder={toDisplayString(t("crm.tenantPlaceholder"))}
                          value={weclappTenant}
                          onChange={(e) => setWeclappTenant(e.target.value)}
                        />
                        <Input
                          type="password"
                          placeholder={toDisplayString(t("crm.apiTokenPlaceholder"))}
                          value={weclappToken}
                          onChange={(e) => setWeclappToken(e.target.value)}
                        />
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={connecting === id}
                          onClick={connectWeclapp}
                        >
                          {connecting === id ? "…" : toSafeReactNode(t("crm.connect"))}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Button variant="ghost" size="sm" disabled={isFetching} onClick={() => refetch()}>
            {isFetching ? toSafeReactNode(t("crm.refreshing")) : toSafeReactNode(t("crm.refreshStatus"))}
          </Button>
        </>
      )}
    </div>
  );
}

export const crmRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "crm",
  component: CrmPage,
});
